import { useCallback, useState } from 'react'
import type { Edge } from '@xyflow/react'
import type { WorkflowNode } from 'app/types/workflows'

type Snapshot = { nodes: WorkflowNode[]; edges: Edge[] }

type Params = {
  nodes: WorkflowNode[]
  edges: Edge[]
  setNodes: (nodes: WorkflowNode[]) => void
  setEdges: (edges: Edge[]) => void
}

const LIMIT = 100

/** Lightweight undo/redo for the canvas. Covers structural changes (add, move, connect,
 *  delete, restore) — call takeSnapshot() right before mutating. Inspector field edits are
 *  intentionally excluded; the inputs have their own native undo. React Flow updates state
 *  immutably, so storing array references in the snapshot is safe. */
export function useUndoRedo({ nodes, edges, setNodes, setEdges }: Params) {
  const [past, setPast] = useState<Snapshot[]>([])
  const [future, setFuture] = useState<Snapshot[]>([])

  const takeSnapshot = useCallback(() => {
    setPast((stack) => [...stack.slice(-(LIMIT - 1)), { nodes, edges }])
    setFuture([])
  }, [nodes, edges])

  const undo = useCallback(() => {
    if (past.length === 0) return
    const previous = past[past.length - 1]
    setFuture((stack) => [{ nodes, edges }, ...stack])
    setPast((stack) => stack.slice(0, -1))
    setNodes(previous.nodes)
    setEdges(previous.edges)
  }, [past, nodes, edges, setNodes, setEdges])

  const redo = useCallback(() => {
    if (future.length === 0) return
    const next = future[0]
    setPast((stack) => [...stack, { nodes, edges }])
    setFuture((stack) => stack.slice(1))
    setNodes(next.nodes)
    setEdges(next.edges)
  }, [future, nodes, edges, setNodes, setEdges])

  return { takeSnapshot, undo, redo, canUndo: past.length > 0, canRedo: future.length > 0 }
}
